/**
 * Register the service worker + prompt the driver to enable push notifications.
 *
 * VAPID public key is fetched from the backend (auto-generated if not in env),
 * so drivers can subscribe out-of-the-box on the preview and production.
 */
import driverApi from "./driverApi";

const SW_PATH = "/sw.js";
const DISMISS_KEY = "juke_push_dismissed_at";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_PATH);
    return reg;
  } catch (err) {
    console.warn("[push] SW registration failed:", err);
    return null;
  }
}

export function pushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  if (!("Notification" in window)) return "denied";
  return Notification.permission;
}

export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * iter60 — Key rotation guard.
 *
 * Compares the existing PushSubscription's applicationServerKey against
 * the current one served by the backend. If they diverge (server key
 * rotated, backend re-generated the VAPID pair, etc.) we silently
 * unsubscribe and re-subscribe with the new key. This prevents the
 * driver from ever seeing "The provided applicationServerKey is not
 * valid" after a backend key rotation.
 *
 * Returns the (possibly refreshed) PushSubscription, or null if the
 * user has never opted in / permission was revoked.
 */
export async function ensurePushKeyInSync() {
  if (!pushSupported()) return null;
  if (Notification.permission !== "granted") return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return null;

  let serverKey;
  try {
    const { data } = await driverApi.get("/driver/push/public-key");
    serverKey = data.public_key;
  } catch (_) {
    // Backend momentarily unreachable — skip the check, don't disturb
    // the existing subscription.
    return sub;
  }
  if (!serverKey) return sub;

  const currentKey = sub.options && sub.options.applicationServerKey
    ? _uint8ToBase64Url(new Uint8Array(sub.options.applicationServerKey))
    : null;

  if (currentKey && currentKey === serverKey) return sub;

  // Divergence — re-subscribe silently with the new key.
  try {
    try { await driverApi.post("/driver/push/unsubscribe", { endpoint: sub.endpoint }); }
    catch (_) { /* server may already be clean */ }
    await sub.unsubscribe();
    const fresh = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(serverKey),
    });
    const json = fresh.toJSON();
    await driverApi.post("/driver/push/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys,
      expiration_time: json.expirationTime || null,
    });
    return fresh;
  } catch (err) {
    console.warn("[push] key-rotation re-subscribe failed", err);
    return sub;
  }
}

function _uint8ToBase64Url(u8) {
  let str = "";
  for (let i = 0; i < u8.length; i++) str += String.fromCharCode(u8[i]);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export async function subscribeToPush() {
  if (!pushSupported()) throw new Error("Push not supported on this device.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }
  const reg = await registerServiceWorker();
  if (!reg) throw new Error("Service worker could not be registered.");

  // Fetch public key
  const { data } = await driverApi.get("/driver/push/public-key");
  const publicKey = data.public_key;
  if (!publicKey) throw new Error("Server did not return a VAPID public key.");

  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  const json = subscription.toJSON();
  await driverApi.post("/driver/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    expiration_time: json.expirationTime || null,
  });
  return subscription;
}

export async function unsubscribeFromPush() {
  const sub = await currentSubscription();
  if (!sub) return;
  try {
    await driverApi.post("/driver/push/unsubscribe", { endpoint: sub.endpoint });
  } catch (_) { /* server may already be clean */ }
  await sub.unsubscribe();
}

export function pushDismissed() {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!ts) return false;
  return Date.now() - ts < 7 * 24 * 60 * 60 * 1000; // 7 days
}

export function dismissPushForNow() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* noop */ }
}
